import { motion, useReducedMotion } from 'motion/react';
import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { OnboardingIntroSkyCloud } from '~/components/v2/components/workspace/onboarding-intro/sky/OnboardingIntroSkyCloud';
import styles from '~/components/v2/components/workspace/onboarding-intro/sky/onboardingIntroSkyClouds.module.css';

type OnboardingIntroSkyCloudSpec = {
  delayS: number;
  driftClassName: string;
  fieldLeftPercent: number;
  fieldY: string;
  /**
   * In `vw`, like the width, so a cloud is the same share of the window at every size. The still is
   * wider than it is tall, and `object-contain` fits it to whichever edge runs out first.
   */
  height: string;
  morphOverlayClassName: string;
  opacity: number;
  swellClassName: string;
  widthPercent: number;
};

// Edges stay clear of the centred copy. Opacities sit low enough for the navy sky to mix into each
// cloud.
const ONBOARDING_INTRO_SKY_CLOUDS: readonly OnboardingIntroSkyCloudSpec[] = [
  {
    delayS: 0,
    driftClassName: styles.driftAcross,
    fieldLeftPercent: -16,
    fieldY: '-2vh',
    height: '34vw',
    morphOverlayClassName: styles.skyCloudMorphOverlay,
    opacity: 0.62,
    swellClassName: styles.lavaSwellFirst,
    widthPercent: 66,
  },
  {
    delayS: 0.08,
    driftClassName: styles.driftAcrossSlow,
    fieldLeftPercent: 64,
    fieldY: '-6vh',
    height: '30vw',
    morphOverlayClassName: styles.skyCloudMorphOverlaySlow,
    opacity: 0.58,
    swellClassName: styles.lavaSwellSecond,
    widthPercent: 58,
  },
  {
    delayS: 0.16,
    driftClassName: styles.driftAcrossSlow,
    fieldLeftPercent: 58,
    fieldY: '-74vh',
    height: '26vw',
    morphOverlayClassName: styles.skyCloudMorphOverlaySlow,
    opacity: 0.5,
    swellClassName: styles.lavaSwellThird,
    widthPercent: 52,
  },
  {
    delayS: 0.24,
    driftClassName: styles.driftAcross,
    fieldLeftPercent: -18,
    fieldY: '-36vh',
    height: '24vw',
    morphOverlayClassName: styles.skyCloudMorphOverlay,
    opacity: 0.54,
    swellClassName: styles.lavaSwellFourth,
    widthPercent: 48,
  },
];

const SKY_CLOUD_ENTER_DURATION_S = 0.9;

export type OnboardingIntroSkyCloudsProps = {
  /** Fade the clouds in. Leave this false to keep the sky clear. */
  shouldEnter?: boolean;
  /**
   * The intro fades the clouds in. A page that mounts this sky while flags and account
   * settle should skip that fade, or each remount reads as the clouds blinking.
   */
  playEnterAnimation?: boolean;
};

export const OnboardingIntroSkyClouds = memo(
  ({ shouldEnter = true, playEnterAnimation = true }: OnboardingIntroSkyCloudsProps) => {
    const shouldReduceMotion = !!useReducedMotion();
    const skipEnterAnimation = shouldReduceMotion || !playEnterAnimation;
    const shouldAnimateEnter = shouldEnter && !shouldReduceMotion && playEnterAnimation;

    return (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
        data-testid="ONBOARDING_INTRO_SKY_CLOUDS"
      >
        <div className={styles.skyMoonWash} />
        {ONBOARDING_INTRO_SKY_CLOUDS.map((cloud) => (
          <motion.div
            animate={
              shouldEnter || shouldReduceMotion
                ? { opacity: cloud.opacity, y: cloud.fieldY }
                : { opacity: 0, y: cloud.fieldY }
            }
            className="absolute bottom-0"
            initial={skipEnterAnimation ? false : { opacity: 0, y: cloud.fieldY }}
            key={`${cloud.fieldLeftPercent}-${cloud.fieldY}`}
            style={{
              height: cloud.height,
              left: `${cloud.fieldLeftPercent}%`,
              width: `${cloud.widthPercent}%`,
            }}
            transition={{
              delay: shouldAnimateEnter ? cloud.delayS : 0,
              duration: shouldAnimateEnter ? SKY_CLOUD_ENTER_DURATION_S : 0,
              ease: 'easeOut',
            }}
          >
            <div className={cn('relative size-full', !shouldReduceMotion && cloud.driftClassName)}>
              <OnboardingIntroSkyCloud
                morphOverlayClassName={cloud.morphOverlayClassName}
                shouldReduceMotion={shouldReduceMotion}
                swellClassName={cloud.swellClassName}
              />
            </div>
          </motion.div>
        ))}
      </div>
    );
  },
);

OnboardingIntroSkyClouds.displayName = 'OnboardingIntroSkyClouds';
